import { useState } from 'react'
import type { LeaveTypeId } from '@lms/shared-types'
import { useViewport } from '../../lib/use-viewport'
import { LegendInfoIcon } from './LegendInfoIcon'

interface CalendarLegendProps {
  /** Only the leave types actually present in the visible window — keeps the legend uncluttered. */
  presentLeaveTypes: ReadonlyArray<LeaveTypeId>
}

const DAY_MARKER_INFO = {
  publicHoliday:
    'A gazetted Malaysia public holiday — no work required, no leave deducted.',
  blackout:
    'A period when leave requests are restricted (e.g. product launch, year-end close). Approvals require Director sign-off.',
} as const

export function CalendarLegend(
  _props: Readonly<CalendarLegendProps>,
): JSX.Element {
  const { isMobile } = useViewport()
  const [mobileExpanded, setMobileExpanded] = useState(false)

  // ── Rows rendered inside the .tc-legend grid ──
  // Each row is two cells: .tc-lab (label) and .tc-items (chips).
  const rows = (
    <>
      {/* Day markers — always shown for reference, even in a month that
          happens to have no holiday or blackout. */}
      <span className="tc-lab">Day markers</span>
      <div className="tc-items">
        <span className="tc-item">
          <span
            aria-hidden
            style={{
              width: 16,
              height: 14,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--danger-bg-3)',
              border: '1px solid var(--danger-line)',
              borderRadius: 3,
              color: 'var(--danger)',
              fontFamily: 'var(--font-mono, inherit)',
              fontSize: 9,
              fontWeight: 700,
            }}
          >
            1
          </span>
          Public holiday
          <LegendInfoIcon
            tooltip={DAY_MARKER_INFO.publicHoliday}
            ariaLabel="What is a public holiday?"
          />
        </span>
        <span className="tc-item">
          <span
            aria-hidden
            style={{
              width: 14,
              height: 10,
              backgroundColor: 'var(--surface)',
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent 0 2.5px, var(--blackout-hatch) 3px, transparent 3.5px 6px)',
              border: '1px solid var(--blackout-line)',
              display: 'inline-block',
              borderRadius: 3,
            }}
          />
          Blackout
          <LegendInfoIcon
            tooltip={DAY_MARKER_INFO.blackout}
            ariaLabel="What is a blackout?"
          />
        </span>
      </div>
    </>
  )

  if (isMobile) {
    return (
      <div data-testid="calendar-legend" style={{ marginTop: 12 }}>
        <button
          type="button"
          data-testid="legend-toggle"
          onClick={() => setMobileExpanded((v) => !v)}
          aria-expanded={mobileExpanded}
          aria-controls="calendar-legend-body"
          className="tc-dd"
        >
          {mobileExpanded ? 'Hide legend' : 'Show legend'} ▾
        </button>
        {mobileExpanded ? (
          <div
            id="calendar-legend-body"
            className="tc-legend"
            style={{
              marginTop: 8,
              padding: 12,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
            }}
          >
            {rows}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div data-testid="calendar-legend" className="tc-legend">
      {rows}
    </div>
  )
}
